import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, CalendarDays, Globe2 } from "lucide-react";
import Footer from "@/components/Footer";
import { BackLink } from "@/components/PageHeader";
import AccountMenu, { SignupLink } from "@/components/AccountMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = ["All", "India", "Asia", "Europe", "North America", "Africa", "Oceania"];
const DISTANCES = ["All", "Marathon", "Half", "Ultra"];

const RACES = [
  { month: "Sep 2026", d: "13 Sep", race: "Ladakh Marathon", city: "Leh, India", region: "India", dist: ["Marathon", "Half", "Ultra 72K"] },
  { month: "Sep 2026", d: "20 Sep", race: "Copenhagen Half Marathon", city: "Copenhagen, Denmark", region: "Europe", dist: ["Half"] },
  { month: "Sep 2026", d: "6 Sep", race: "Satara Hill Half Marathon", city: "Satara, India", region: "India", dist: ["Half"] },
  { month: "Sep 2026", d: "27 Sep", race: "Berlin Marathon", city: "Berlin, Germany", region: "Europe", dist: ["Marathon"] },
  { month: "Oct 2026", d: "4 Oct", race: "Sekhon IAF Half Marathon", city: "Mumbai, India", region: "India", dist: ["Half"] },
  { month: "Oct 2026", d: "4 Oct", race: "Cardiff Half Marathon", city: "Cardiff, UK", region: "Europe", dist: ["Half"] },
  { month: "Oct 2026", d: "11 Oct", race: "Chicago Marathon", city: "Chicago, USA", region: "North America", dist: ["Marathon"] },
  { month: "Oct 2026", d: "18 Oct", race: "Vedanta Delhi Half Marathon", city: "New Delhi, India", region: "India", dist: ["Half"] },
  { month: "Oct 2026", d: "18 Oct", race: "Amsterdam Marathon", city: "Amsterdam, Netherlands", region: "Europe", dist: ["Marathon", "Half"] },
  { month: "Oct 2026", d: "18 Oct", race: "Toronto Waterfront Marathon", city: "Toronto, Canada", region: "North America", dist: ["Marathon", "Half"] },
  { month: "Oct 2026", d: "25 Oct", race: "Valencia Half Marathon", city: "Valencia, Spain", region: "Europe", dist: ["Half"] },
  { month: "Nov 2026", d: "1 Nov", race: "Winter Delhi Half Marathon", city: "New Delhi, India", region: "India", dist: ["Half", "10K"] },
  { month: "Nov 2026", d: "1 Nov", race: "TCS New York City Marathon", city: "New York, USA", region: "North America", dist: ["Marathon"] },
  { month: "Nov 2026", d: "8 Nov", race: "Athens Marathon — The Authentic", city: "Athens, Greece", region: "Europe", dist: ["Marathon"] },
  { month: "Nov 2026", d: "1 Nov", race: "Istanbul Marathon", city: "Istanbul, Türkiye", region: "Europe", dist: ["Marathon", "Half"] },
  { month: "Nov 2026", d: "15 Nov", race: "Chandigarh Half Marathon", city: "Chandigarh, India", region: "India", dist: ["Half", "10K"] },
  { month: "Nov 2026", d: "28 Nov", race: "Baramati Power Marathon", city: "Baramati, India", region: "India", dist: ["Half", "10K"] },
  { month: "Dec 2026", d: "6 Dec", race: "Valencia Marathon", city: "Valencia, Spain", region: "Europe", dist: ["Marathon"] },
  { month: "Dec 2026", d: "6 Dec", race: "Standard Chartered Singapore Marathon", city: "Singapore", region: "Asia", dist: ["Marathon", "Half"] },
  { month: "Dec 2026", d: "13 Dec", race: "Bajaj Pune Marathon", city: "Pune, India", region: "India", dist: ["Half", "10K"] },
  { month: "Dec 2026", d: "20 Dec", race: "Kolkata 25K", city: "Kolkata, India", region: "India", dist: ["25K"] },
  { month: "Jan 2027", d: "17 Jan", race: "Tata Mumbai Marathon", city: "Mumbai, India", region: "India", dist: ["Marathon", "Half"] },
  { month: "Jan 2027", d: "17 Jan", race: "Dubai Marathon", city: "Dubai, UAE", region: "Asia", dist: ["Marathon", "Half"] },
  { month: "Jan 2027", d: "23 Jan", race: "Buriram Marathon", city: "Buriram, Thailand", region: "Asia", dist: ["Marathon", "Half"] },
  { month: "Feb 2027", d: "14 Feb", race: "Federal Bank Kochi Marathon", city: "Kochi, India", region: "India", dist: ["Half", "10K"] },
  { month: "Feb 2027", d: "28 Feb", race: "Osaka Marathon", city: "Osaka, Japan", region: "Asia", dist: ["Marathon"] },
  { month: "Mar 2027", d: "7 Mar", race: "Tokyo Marathon", city: "Tokyo, Japan", region: "Asia", dist: ["Marathon"] },
  { month: "Mar 2027", d: "7 Mar", race: "Lisbon Half Marathon", city: "Lisbon, Portugal", region: "Europe", dist: ["Half"] },
  { month: "Mar 2027", d: "21 Mar", race: "Rome Marathon", city: "Rome, Italy", region: "Europe", dist: ["Marathon"] },
  { month: "Mar 2027", d: "14 Mar", race: "Barcelona Marathon", city: "Barcelona, Spain", region: "Europe", dist: ["Marathon"] },
  { month: "Apr 2027", d: "3 Apr", race: "Prague Half Marathon", city: "Prague, Czechia", region: "Europe", dist: ["Half"] },
  { month: "Apr 2027", d: "4 Apr", race: "Berlin Half Marathon", city: "Berlin, Germany", region: "Europe", dist: ["Half"] },
  { month: "Apr 2027", d: "4 Apr", race: "Paris Marathon", city: "Paris, France", region: "Europe", dist: ["Marathon"] },
  { month: "Apr 2027", d: "19 Apr", race: "Boston Marathon", city: "Boston, USA", region: "North America", dist: ["Marathon"] },
  { month: "Apr 2027", d: "25 Apr", race: "London Marathon", city: "London, UK", region: "Europe", dist: ["Marathon"] },
  { month: "May 2027", d: "16 May", race: "Copenhagen Marathon", city: "Copenhagen, Denmark", region: "Europe", dist: ["Marathon", "Half"] },
  { month: "May 2027", d: "15 May", race: "Great Wall Marathon", city: "Beijing, China", region: "Asia", dist: ["Marathon", "Half"] },
  { month: "Jun 2027", d: "13 Jun", race: "Comrades Marathon", city: "KwaZulu-Natal, South Africa", region: "Africa", dist: ["Ultra 89K"] },
  { month: "Jun 2027", d: "19 Jun", race: "Midnight Sun Marathon", city: "Tromsø, Norway", region: "Europe", dist: ["Marathon", "Half"] },
  { month: "Jul 2027", d: "3 Jul", race: "Gold Coast Marathon", city: "Gold Coast, Australia", region: "Oceania", dist: ["Marathon", "Half"] },
  { month: "Aug 2026", d: "29–30 Aug", race: "Hyderabad Marathon", city: "Hyderabad, India", region: "India", dist: ["Marathon", "Half"] },
];

export default function RaceCalendar() {
  const [region, setRegion] = useState("All");
  const [distance, setDistance] = useState("All");
  const [races, setRaces] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/races`)
      .then((r) => setRaces(r.data))
      .catch(() => setRaces(RACES));
  }, []);

  const visible = (races || []).filter(
    (r) =>
      (region === "All" || r.region === region) &&
      (distance === "All" || r.dist.some((d) => d.includes(distance)))
  );

  const months = [...new Set(visible.map((r) => r.month))];

  const enquire = (r) => () =>
    sessionStorage.setItem("fg_trip", `${r.race} (${r.d} ${r.month})`);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-[#FBF9F5]/75 border-b border-[#0D1117]/10">
        <nav className="px-6 sm:px-12 lg:px-20 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" data-testid="calendar-brand-logo" className="group flex items-center">
            <img src="/logo-horizontal.png" alt="Fitygrity" className="h-8 sm:h-9 w-auto object-contain rounded-md transition-opacity duration-300 group-hover:opacity-80" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <SignupLink
              testId="header-link-signup"
              className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] whitespace-nowrap text-[#FF4E00] hover:text-[#E04000] transition-colors duration-300"
            />
            <AccountMenu />
            <BackLink testId="calendar-back-link" />
          </div>
        </nav>
      </header>

      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <Globe2 className="w-4 h-4" /> {races?.length ? `${races[0].month} — ${races[races.length - 1].month}` : "Race Calendar"}
          </p>
          <h1
            data-testid="calendar-headline"
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.06]"
          >
            The Race <span className="italic text-[#FF4E00]">Calendar</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed">
            AIMS-certified marathons, halves and ultras worth travelling for — sourced from the AIMS
            and world's marathon calendars. Tell us which start line, and we'll build the trip.
          </p>
        </motion.div>

        <div className="flex flex-col gap-4 mb-14" data-testid="calendar-filters">
          <div className="flex flex-wrap gap-2">
            {REGIONS.map((r) => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                data-testid={`calendar-region-${r.toLowerCase().replace(/\s/g, "-")}`}
                className={`font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-all duration-300 ${
                  region === r
                    ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                    : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {DISTANCES.map((d) => (
              <button
                key={d}
                onClick={() => setDistance(d)}
                data-testid={`calendar-distance-${d.toLowerCase()}`}
                className={`font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-all duration-300 ${
                  distance === d
                    ? "bg-[#0D1117] border-[#0D1117] text-[#FBF9F5]"
                    : "border-[#0D1117]/15 text-[#374151] hover:border-[#0D1117]/50 hover:text-[#0D1117]"
                }`}
              >
                {d === "All" ? "All Distances" : d}
              </button>
            ))}
          </div>
        </div>

        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-8">
          {races === null ? "Loading the calendar..." : `${visible.length} races · ${months.length} months`}
        </p>

        <div className="space-y-14">
          {months.map((m) => (
            <section key={m}>
              <h2 className="font-serif italic text-xl sm:text-2xl text-[#FF4E00] mb-5">{m}</h2>
              <div className="border-t border-[#0D1117]/10">
                {visible
                  .filter((r) => r.month === m)
                  .map((r) => (
                    <motion.div
                      key={r.race}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      data-testid="calendar-race-row"
                      className="group grid grid-cols-1 sm:grid-cols-[90px_1fr_auto] lg:grid-cols-[110px_1fr_auto_auto] gap-2 sm:gap-6 items-center py-5 border-b border-[#0D1117]/10 hover:bg-[#FFFFFF]/60 sm:px-4 transition-colors duration-300 rounded-lg"
                    >
                      <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#FF4E00] flex items-center gap-2">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {r.d}
                      </span>
                      <div>
                        <p className="font-serif text-lg sm:text-xl text-[#0D1117] group-hover:text-[#FF4E00] transition-colors duration-300">
                          {r.race}
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-1">
                          {r.city}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {r.dist.map((d) => (
                          <span
                            key={d}
                            className="font-mono text-[9px] uppercase tracking-[0.12em] border border-[#0D1117]/15 text-[#374151] rounded-full px-2.5 py-1"
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                      {r.reg_url && (
                        <a
                          href={r.reg_url}
                          target="_blank"
                          rel="noopener"
                          data-testid="calendar-register-link"
                          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#FBF9F5] bg-[#FF4E00] hover:bg-[#E04000] rounded-full px-4 py-2 transition-colors duration-300"
                        >
                          Register
                          {r.reg_url.includes("ahotu") && " · Ahotu"}
                          {r.reg_url.includes("indiarunning") && " · IndiaRunning"}
                          {r.reg_url.includes("ifinish") && " · iFinish"}
                          {r.reg_url.includes("official") && " · Official"}
                          {r.reg_url.includes("ladakhmarathon") && " · Official"}
                          {r.reg_url.includes("runizen") && " · Runizen"}
                          {r.reg_url.includes("konfhub") && " · KonfHub"}
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <a
                        href="/#enquire"
                        onClick={enquire(r)}
                        data-testid="calendar-enquire-link"
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#FF4E00] hover:gap-3 transition-all duration-300"
                      >
                        Enquire <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    </motion.div>
                  ))}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-16 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] max-w-2xl leading-relaxed">
          Dates are checked against IndiaRunning, iFinish, Ahotu, BookMyShow and official event sites —
          organisers occasionally shift race days; final dates, bibs and pricing confirmed on enquiry.
        </p>
      </main>

      <Footer />
    </div>
  );
}
